import InputField from 'components/InputField';
import PluscircleIcon from 'icons/Pluscircle.icon';
import { Button } from 'ncoded-component-library';
import React, { useContext } from 'react';
import { Field, useField } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { useTranslation } from 'react-i18next';
import { WhenTwoFieldChanges } from '../WhenTwoFieldChanges';
import { calculatePriceDifference } from '../../utils';
import TrashIcon from 'icons/Trash.icon';
import { ListingAdjustments } from 'models/Listing';
import PatchListingContext from '../../providers/PatchListing/PatchListing.context';

import './AdjustmentsArray.styles.scss';
import './AdjustmentsArray.styles.responsive.scss';

const MAX_LENGTH = 20;

const AdjustmentsArray = () => {
  const { t } = useTranslation();

  const {
    input: { value: adjustments },
  } = useField<ListingAdjustments[]>('adjustments');

  const { patchListingValue } = useContext(PatchListingContext);

  return (
    <div className="adjustments-array-field">
      <p>{t('adjustments')}</p>
      <FieldArray<ListingAdjustments> name="adjustments">
        {({ fields }) => {
          return (
            <>
              {fields.map((name, index) => {
                const field = fields?.value[index];

                const showReuqired =
                  !!field?.name ||
                  !!field?.grossAmount ||
                  !!field?.netAmount ||
                  !!field?.addBack;

                return (
                  <div key={name} className="adjustments-array-field__fields">
                    <Field
                      name={`${name}.name`}
                      label={t('itemName')}
                      maxLength={100}
                      component={InputField}
                      {...(showReuqired && {
                        required: true,
                      })}
                      customOnBlur={(
                        _: React.FocusEvent<HTMLTextAreaElement>,
                        isValid: boolean,
                      ) => {
                        if (isValid) {
                          patchListingValue({
                            adjustments,
                          });
                        }
                      }}
                    />
                    <Field
                      name={`${name}.grossAmount`}
                      label={t('grossAmount')}
                      placeholder="$"
                      numberStyle="dottedNumber"
                      component={InputField}
                      infoPopupContent={t('grossAmountPopup')}
                      customOnBlur={(
                        _: React.FocusEvent<HTMLTextAreaElement>,
                        isValid: boolean,
                      ) => {
                        if (isValid) {
                          patchListingValue({
                            adjustments,
                          });
                        }
                      }}
                    />
                    <Field
                      name={`${name}.addBack`}
                      label={t('addBack')}
                      placeholder="$"
                      numberStyle="dottedNumber"
                      component={InputField}
                      infoPopupContent={t('addBackPopup')}
                      customOnBlur={(
                        _: React.FocusEvent<HTMLTextAreaElement>,
                        isValid: boolean,
                      ) => {
                        if (isValid) {
                          patchListingValue({
                            adjustments,
                          });
                        }
                      }}
                    />

                    <WhenTwoFieldChanges
                      name={`${name}.netAmount`}
                      nameFrom={`${name}.grossAmount`}
                      nameTo={`${name}.addBack`}
                      calculateNewValue={calculatePriceDifference}
                    />
                    <Field
                      name={`${name}.netAmount`}
                      label={t('netAmount')}
                      placeholder="$"
                      numberStyle="dottedNumber"
                      readOnly
                      component={InputField}
                    />

                    <Button
                      variant="outline"
                      className="adjustments-array-field__fields__delete"
                      disabled={fields.length === 1}
                      onClick={() => {
                        fields.remove(index);

                        const newFields = [...adjustments];
                        newFields.splice(index, 1);

                        patchListingValue({
                          adjustments: newFields,
                        });
                      }}
                    >
                      <TrashIcon />
                    </Button>
                  </div>
                );
              })}
              {fields.length < MAX_LENGTH && (
                <Button
                  variant="outline"
                  onClick={() => {
                    fields.push({
                      name: '',
                      grossAmount: undefined,
                      addBack: undefined,
                      netAmount: undefined,
                    });
                  }}
                >
                  <PluscircleIcon />
                  {t('addAnother')}
                </Button>
              )}
            </>
          );
        }}
      </FieldArray>
    </div>
  );
};

export default AdjustmentsArray;
